import { SettingValueType } from '@Shared.Angular/@types/core/contracts/constants/settingValueTypes';
import LoginSettings from '@Shared.Angular/@types/loginSettings';
import angular from 'angular';
import { AppInsightsConfig } from './appInsights.service';
import { toCamelCaseKey } from './case-converter';

class APP_CONFIG {
  public ready: Promise<void>;
  private setReady: () => void;

  constructor() {
    this.ready = new Promise((resolve) => (this.setReady = resolve));
  }

  auth0ClientId: string;
  enableWebhooks: boolean;
  recaptchaSiteKey: string;
  sessionTimeoutInSecond = 14400;
  enableSessionTimeoutReminder = false;
  sessionTimeoutReminderTriggerInSecond = 300;
  supportHours = '';
  outOfHoursSupportMessage = '';
  apiBaseUrl: string;
  flowModelSizeLimit: number;
  enableStepTasks: boolean;
  allowStepRules: boolean;
  flowinglyEnvironment: string; // Are we in dev, staging, or prod which use unique namespaces for local storage
  brandingLogoWidth: number;
  brandingLogoHeight: number;
  hideSupportButton: boolean;
  enableTenantSwitching: boolean;
  enableTemplateLibrary: boolean;
  enableFlowModelCompare: boolean;
  enableCreationHub: boolean;
  brandingName: string;
  brandingModelerHelpUri: string;
  brandingHelpUri: string;
  brandingHelpTitle: string;
  keyForAuthLoggingId: string;
  disableEveryoneMentions: boolean;
  defaultDays: number;
  enableConsoleLogging: boolean;
  cfEnableConditionalRules: boolean;
  cfDecisionFieldTypesAllowed: string;
  cfActionFieldTypesDisallowed: string;
  auth0Domain = '';
  brandingEmailLogo = '';
  brandingSendingEmailAddress = '';
  disableXssValidation = false;
  showConfidentialFlowOption = false;
  enableCustomPublicFormSubjects = false;
  enableModelerXmlExport = false;
  enableTableFormula = false;
  flowModelFeedbackVisibility = 'flowowners';
  fmoCanPublish = false;
  modelerUrl = '';
  runnerUrl = window.origin?.replace('localhost:9001', 'localhost:9000');
  showLogoFields = false;
  signalrServerUrl = '';
  stepTimeCostVisibility = 'Both';
  showVisibilityToggleInsideConfiguration = false;
  enableConditionalForms = false;
  iPaaSSupportedStepTypes = '';
  enableIPaaSIntegration = false;
  welcomeText = 'Welcome';
  enableDatabaseFeature = false;
  allowProcessMap = false;
  maxSubCategories = 0;
  enableAuthenticationLogging = false;
  enableAppInsightsRunner = false;
  enableLookupFieldCurrentActorOptions: boolean;
  enableProcedureAiEnhancement: boolean;
  googleTagManagerId = '';
  runnerClosedLogoUrl = '';
  runnerOpenLogoUrl = '';
  runnerTitle = '';
  showReportDatesInUtc = false;
  auth0UseUniversalLogin = false;
  intercomId = '';
  intercomUrl = '';
  isDatabaseFeatureEnabled = false;
  localDateTimeFormat = 'DD/MM/YYYY h:mm:ss a';
  maximumWebhooksOnStep = 5;
  maxReportReturnRows = 1000;
  favIcon: string;
  flowFilterMaxCharacters = 70;
  gridReportPageSize = 25;
  hideInviteUsers = false;
  loginAlertMessage = '';
  enableTemplateManagement: boolean;
  allowProcessMapsAsComponents: boolean;
  brandingHomepageUri = '';
  brandingSupportEmail = '';
  disableAdminCategoryEdit = false;
  disableAdminDatabaseDelete = false;
  disableAdminTeamEdit = false;
  disableAdminUserEdit = false;
  disableFlowModelCreation: boolean;
  defaultHomePage = 'flowsactive';
  enableExago = false;
  enableReportOwner = false;
  logoUrl: string;
  exagoBaseUrl: string;
  sparkPostCustomEmailFrom: string;
  maxNumberOfDaysForRecentlyUpdated: number;
  showDashboardsV1: boolean;
  enablePublicProcessMaps = true;
  workflowParserVersion: number;
  displayUsersDropdownInSendTestEmail = false;
  enableConfidentialStep: boolean;
  userWebPushNotificationsEnabled: boolean;
  enableWebPushNotificationsMobile: boolean;
  enableWebPushNotificationsNonMobile: boolean;
  webPushPublicKey: string;
  enableFlowDelete: boolean = undefined;
  auth0UseRefreshToken = false;
  auth0UseSilentSso = false;
  auth0AppV2ClientId: string;
  enableIframePopupLogin = false;
  flowsInDateFilterDefaultRange: number;
  flowsInDateFilterMaxRange: number;
  enableFormFieldReCaptcha: boolean = undefined;
  enableConnectorPoints: boolean;
  enableGovernance: boolean;
  enableMappingFieldsOnly: boolean;
  enableTimeCost: boolean = undefined;
  averageCostPerHour: string;
  enableExternalUserStep: boolean;
  flowModelReviewWindowDays: number;
  enableHiddenLookupField: boolean;
  enableMapAutomation: boolean;
  enableCancelledFlowsView: boolean;
  enableSetupRoles: boolean;
  enableTestFlows: boolean;
  enableSetupFeatures: boolean;
  enableVideoRecording: boolean;
  enableBackendDocumentPrinting: boolean;
  enableFlowModelGeneration: boolean;
  enableVideoToXGeneration: boolean;
  enableAudioToXGeneration: boolean;
  allowWorkflow = true;
  enableTagging = false;
  maxReturnRecords: number;
  enableProcedures: boolean;
  enableRecurringStepReminders: boolean;
  maxNumberOfStepReminders: number;
  enablePublicFormsAnonymous: boolean;
  enablePublicFormsSignIn: boolean;
  enablePublishNotificationOption: boolean;
  enableFormFieldAddress: boolean;
  enableFormFieldBusiness: boolean;
  enableFieldIcons: boolean;
  enableFlowModelLoopValidation: boolean;
  userGridConfigs: string;
  processMapDemoUrl: string;
  procedureDemoUrl: string;
  workflowDemoUrl: string;
  enableModelerTabs: boolean;
  enableFutureStates = false;
  enableStakeholders: boolean;
  enableAutosaveAndVersionRestore: boolean;
  autoSaveIntervalMinutes: number;
  customEmailAllowedHyperlinkPrefixes: string;
  appInsightsRunnerConfig: AppInsightsConfig | undefined;
  enableSmartSearch: boolean;
  enableWorkflowStepAssistant: boolean;
  enableWritingAssist: boolean;
  enableCircleCommunity: boolean;

  runnerRehomeUrl = '';

  exagoConfig = {
    defaultDashboard: {
      folder: 'Dashboards',
      name: 'My Consumption Dashboard'
    }
  };

  intercomConfig = {
    id: '',
    pollDelay: 5000
  };

  // settings from settings.json that are not overridden with login or business settings
  noOverwriteSettings: string[] = [];
  isSettingOverwritable(settingName: string): boolean {
    const exist = this.noOverwriteSettings.includes(settingName);
    return !exist;
  }

  isTrueString(value: unknown) {
    return typeof value === 'string' && value.toLowerCase().trim() === 'true';
  }

  settings;
  public updateSetting(
    settingName: string,
    settingValue: string,
    settingValueType = SettingValueType.string,
    propertyName: string | null = null
  ) {
    if (propertyName === null) {
      propertyName = settingName.replace(/[^A-Za-z0-9]*/g, '');
      propertyName =
        propertyName.charAt(0).toLowerCase() + propertyName.slice(1);
    }

    if (
      typeof settingValue !== 'string' ||
      !this.isSettingOverwritable(propertyName)
    ) {
      return;
    }

    let convertedValue: string | boolean | number;

    switch (settingValueType) {
      case SettingValueType.boolean:
        convertedValue = this.isTrueString(settingValue);
        break;
      case SettingValueType.number:
        convertedValue = Number(settingValue);
        if (isNaN(convertedValue)) {
          this.throwParseError(settingName, settingValue);
        }
        break;
      case SettingValueType.string:
      default:
        convertedValue = settingValue;
        break;
    }

    this[propertyName] = convertedValue;
  }

  private throwParseError(settingName: string, settingValue: string): never {
    throw new Error(
      `Failed to parse setting '${settingName}': '${settingValue}'`
    );
  }

  private tryApplySetting(
    settingName: string,
    settingValueType = SettingValueType.string,
    propertyName = null
  ) {
    const value = this.getSettingValue(settingName);
    this.updateSetting(settingName, value, settingValueType, propertyName);
  }

  /**
   * Reads a value from the account/settings response by its backend setting
   * name. The response keys are camelCased by the HTTP interceptor, so the name
   * is converted with the same rule before lookup. This lets callers keep using
   * the canonical backend setting name (e.g. 'WorkflowParserVersion',
   * 'Branding:HelpUri') without hardcoding the converted key.
   */
  private getSettingValue(settingName: string) {
    return this.settings[toCamelCaseKey(settingName)];
  }

  /**
   * Pass data from window.settings to local fields
   * @param settings
   */
  public populateFromSettingsJSON(settings: object) {
    // clear noOverwriteSettings
    this.noOverwriteSettings = [];

    for (const [key, value] of Object.entries(settings)) {
      this[key] = value;
      this.noOverwriteSettings.push(key);
    }
  }

  /**
   * Populate data from /settings/login?domainname api
   * @param loginSettings
   */
  public populateLoginSettings(loginSettings: LoginSettings) {
    const {
      welcomeText,
      loginAlertMessage,
      auth0UseUniversalLogin,
      logoUrl,
      title,
      favIcon,
      sessionTimeoutInSecond,
      auth0UseRefreshToken,
      auth0UseSilentSso,
      enableIframePopupLogin,
      logging: {
        enableAuthenticationLogging,
        enableAppInsightsRunner,
        appInsightsRunnerConfig
      }
    } = loginSettings;
    if (this.isSettingOverwritable('welcomeText')) {
      this.welcomeText = welcomeText;
    }

    if (this.isSettingOverwritable('loginAlertMessage')) {
      this.loginAlertMessage = loginAlertMessage;
    }

    if (this.isSettingOverwritable('auth0UseUniversalLogin')) {
      this.auth0UseUniversalLogin = auth0UseUniversalLogin;
    }

    if (this.isSettingOverwritable('auth0UseRefreshToken')) {
      this.auth0UseRefreshToken = auth0UseRefreshToken;
    }

    if (this.isSettingOverwritable('auth0UseSilentSso')) {
      this.auth0UseSilentSso = auth0UseSilentSso;
    }

    if (this.isSettingOverwritable('enableIframePopupLogin')) {
      this.enableIframePopupLogin = enableIframePopupLogin;
    }

    if (this.isSettingOverwritable('logoUrl')) {
      this.logoUrl = logoUrl;
    }

    if (this.isSettingOverwritable('enableAppInsightsRunner')) {
      this.enableAppInsightsRunner = enableAppInsightsRunner;
    }

    if (this.isSettingOverwritable('enableAuthenticationLogging')) {
      this.enableAuthenticationLogging = enableAuthenticationLogging;
    }

    if (this.isSettingOverwritable('appInsightsRunnerConfig')) {
      this.appInsightsRunnerConfig = appInsightsRunnerConfig;
    }

    if (this.isSettingOverwritable('runnerTitle')) {
      this.runnerTitle = title;
    }

    if (this.isSettingOverwritable('favIcon')) {
      this.favIcon = favIcon;
    }

    if (this.isSettingOverwritable('sessionTimeoutInSecond')) {
      this.sessionTimeoutInSecond = sessionTimeoutInSecond;
    }
  }

  /**
   * Populate data from account/settings api
   * @param settings
   */
  public populateSettings(settings) {
    this.settings = settings;

    // Ideally we would provide the setting type so we could iterate over settings
    // instead of having to define each setting like this.

    // In the meantime we should use tryApplySetting to keep things simple
    this.tryApplySetting('EnableTagging', SettingValueType.boolean);
    this.tryApplySetting(
      'EnableBackendDocumentPrinting',
      SettingValueType.boolean
    );
    this.tryApplySetting('AllowWorkflow', SettingValueType.boolean);
    this.tryApplySetting(
      'SessionTimeoutReminderTriggerInSecond',
      SettingValueType.number
    );
    this.tryApplySetting(
      'EnableSessionTimeoutReminder',
      SettingValueType.boolean
    );
    this.tryApplySetting('EnableSetupRoles', SettingValueType.boolean);
    this.tryApplySetting('EnableSetupFeatures', SettingValueType.boolean);
    this.tryApplySetting('EnableTemplateLibrary', SettingValueType.boolean);
    this.tryApplySetting('EnableTemplateManagement', SettingValueType.boolean);
    this.tryApplySetting('DisableFlowModelCreation', SettingValueType.boolean);
    this.tryApplySetting('DisableEveryoneMentions', SettingValueType.boolean);
    this.tryApplySetting('Branding:HomepageUri');
    this.tryApplySetting('Branding:SupportEmail');
    this.tryApplySetting('Branding:Logo:Width', SettingValueType.number);
    this.tryApplySetting('Branding:Logo:Height', SettingValueType.number);
    this.tryApplySetting(
      'AllowProcessMapsAsComponents',
      SettingValueType.boolean
    );

    this.tryApplySetting('AllowStepRules', SettingValueType.boolean);
    this.tryApplySetting('Branding:Name');
    this.tryApplySetting('Branding:HelpTitle');
    this.tryApplySetting('Branding:ModelerHelpUri');
    this.tryApplySetting('Branding:Email:Logo');
    this.tryApplySetting(
      'Branding:Email:SendingEmailAddress',
      SettingValueType.string,
      'brandingSendingEmailAddress'
    );
    this.tryApplySetting(
      'SparkPost.CustomEmailFrom',
      SettingValueType.string,
      'sparkPostCustomEmailFrom'
    );
    this.tryApplySetting('DisableXssValidation', SettingValueType.boolean);
    this.tryApplySetting('EnableConditionalForms', SettingValueType.boolean);
    this.tryApplySetting('EnableConsoleLogging', SettingValueType.boolean);
    this.tryApplySetting(
      'EnableCustomPublicFormSubjects',
      SettingValueType.boolean
    );
    this.tryApplySetting('EnableModelerXmlExport', SettingValueType.boolean);
    this.tryApplySetting('EnableFlowModelGeneration', SettingValueType.boolean);
    this.tryApplySetting('EnableVideoToXGeneration', SettingValueType.boolean);
    this.tryApplySetting('EnableAudioToXGeneration', SettingValueType.boolean);
    this.tryApplySetting('EnableSmartSearch', SettingValueType.boolean);
    this.tryApplySetting(
      'EnableWorkflowStepAssistant',
      SettingValueType.boolean
    );
    this.tryApplySetting('EnableWritingAssist', SettingValueType.boolean);
    this.tryApplySetting('EnableStepTasks', SettingValueType.boolean);
    this.tryApplySetting('EnableTableFormula', SettingValueType.boolean);
    this.tryApplySetting('EnableWebhooks', SettingValueType.boolean);
    this.tryApplySetting('HideSupportButton', SettingValueType.boolean);
    this.tryApplySetting('EnableModelerTabs', SettingValueType.boolean);
    this.tryApplySetting('EnableFutureStates', SettingValueType.boolean);
    this.tryApplySetting('EnableStakeholders', SettingValueType.boolean);
    this.tryApplySetting(
      'EnableFlowModelLoopValidation',
      SettingValueType.boolean
    );
    this.tryApplySetting('OutOfHoursSupportMessage');
    this.tryApplySetting(
      'ShowConfidentialFlowOption',
      SettingValueType.boolean
    );
    this.tryApplySetting('ShowLogoFields', SettingValueType.boolean);
    this.tryApplySetting(
      'ShowVisibilityToggleInsideConfiguration',
      SettingValueType.boolean
    );
    this.tryApplySetting('SupportHours');
    this.tryApplySetting('auth0:ClientId');
    this.tryApplySetting('auth0:Domain');
    this.tryApplySetting('UserGridConfigs', SettingValueType.string);
    this.tryApplySetting(
      'UserWebPushNotificationsEnabled',
      SettingValueType.boolean
    );
    this.tryApplySetting(
      'EnableWebPushNotificationsMobile',
      SettingValueType.boolean
    );
    this.tryApplySetting(
      'EnableWebPushNotificationsNonMobile',
      SettingValueType.boolean
    );
    this.tryApplySetting('WebPushPublicKey');

    this.tryApplySetting(
      'FMOCanPublish',
      SettingValueType.boolean,
      'fmoCanPublish'
    );
    this.tryApplySetting(
      'CF:DecisionFieldTypesAllowed',
      SettingValueType.string,
      'cfDecisionFieldTypesAllowed'
    );
    this.tryApplySetting(
      'CF:EnableConditionalRules',
      SettingValueType.boolean,
      'cfEnableConditionalRules'
    );
    this.tryApplySetting(
      'CF:ActionFieldTypesDisallowed',
      SettingValueType.string,
      'cfActionFieldTypesDisallowed'
    );
    this.tryApplySetting('ShowDashboardsV1', SettingValueType.boolean);
    this.tryApplySetting(
      'DisplayUsersDropdownInSendTestEmail',
      SettingValueType.boolean
    );
    this.tryApplySetting('EnableConfidentialStep', SettingValueType.boolean);
    this.tryApplySetting('EnableFlowModelCompare', SettingValueType.boolean);
    this.tryApplySetting('EnableCreationHub', SettingValueType.boolean);
    this.tryApplySetting('EnableConnectorPoints', SettingValueType.boolean);
    this.tryApplySetting('EnableGovernance', SettingValueType.boolean);
    this.tryApplySetting('EnableVideoRecording', SettingValueType.boolean);
    this.tryApplySetting(
      'EnableLookupFieldCurrentActorOptions',
      SettingValueType.boolean
    );
    this.tryApplySetting('EnableMapAutomation', SettingValueType.boolean);
    this.tryApplySetting('EnableProcedures', SettingValueType.boolean);
    this.tryApplySetting('EnableMappingFieldsOnly', SettingValueType.boolean);
    this.tryApplySetting('EnableTimeCost', SettingValueType.boolean);
    this.tryApplySetting('AverageCostPerHour');
    this.tryApplySetting('EnableExternalUserStep', SettingValueType.boolean);
    this.tryApplySetting('FlowModelReviewWindowDays');
    this.tryApplySetting('EnableCancelledFlowsView', SettingValueType.boolean);
    this.tryApplySetting('EnableTestFlows', SettingValueType.boolean);
    this.tryApplySetting(
      'EnableRecurringStepReminders',
      SettingValueType.boolean
    );
    this.tryApplySetting('MaxNumberOfStepReminders', SettingValueType.number);
    this.tryApplySetting(
      'MaxNumberOfDaysForRecentlyUpdated',
      SettingValueType.number
    );
    this.tryApplySetting(
      'EnableProcedureAiEnhancement',
      SettingValueType.boolean
    );
    this.tryApplySetting('ProcessMapDemoUrl', SettingValueType.string);
    this.tryApplySetting('ProcedureDemoUrl', SettingValueType.string);
    this.tryApplySetting('WorkflowDemoUrl', SettingValueType.string);
    this.tryApplySetting(
      'CustomEmailAllowedHyperlinkPrefixes',
      SettingValueType.string
    );
    this.tryApplySetting(
      'EnableAutosaveAndVersionRestore',
      SettingValueType.boolean
    );
    this.tryApplySetting('AutoSaveIntervalMinutes', SettingValueType.number);

    if (
      this.getSettingValue('WorkflowParserVersion') &&
      this.isSettingOverwritable('workflowParserVersion')
    ) {
      this.workflowParserVersion = +this.getSettingValue(
        'WorkflowParserVersion'
      );
    }
    if (
      this.getSettingValue('Flowingly.Core.ServiceCatalog.Api') &&
      this.isSettingOverwritable('apiBaseUrl')
    ) {
      this.apiBaseUrl =
        this.getSettingValue('Flowingly.Core.ServiceCatalog.Api') + '/api/';
    }

    if (
      this.getSettingValue('FlowModelFeedbackVisibility') &&
      this.isSettingOverwritable('flowModelFeedbackVisibility')
    ) {
      this.flowModelFeedbackVisibility = this.getSettingValue(
        'FlowModelFeedbackVisibility'
      ).toLowerCase();
    }

    if (this.getSettingValue('IntercomPollDelay')) {
      const value = +this.getSettingValue('IntercomPollDelay');
      if (Number.isFinite(value) && value > 0) {
        this.intercomConfig.pollDelay = value;
      } else {
        console.error(
          `Invalid IntercomPollDelay of ${value}. Using ${this.intercomConfig.pollDelay}`
        );
      }
    }

    if (
      this.getSettingValue('SessionTimeoutInSecond') &&
      this.isSettingOverwritable('sessionTimeoutInSecond')
    ) {
      this.sessionTimeoutInSecond = +this.getSettingValue(
        'SessionTimeoutInSecond'
      );
    }

    if (
      this.getSettingValue('StepTimeCostVisibility') &&
      this.isSettingOverwritable('stepTimeCostVisibility')
    ) {
      this.stepTimeCostVisibility = this.getSettingValue(
        'StepTimeCostVisibility'
      ).toLowerCase();
    }

    if (this.isSettingOverwritable('iPaaSSupportedStepTypes')) {
      this.iPaaSSupportedStepTypes =
        this.getSettingValue('IPaaSSupportedStepTypes') || '';
    }

    if (this.isSettingOverwritable('EnableIPaaSIntegration')) {
      this.enableIPaaSIntegration = this.isTrueString(
        this.getSettingValue('EnableIPaaSIntegration')
      );
    }

    if (this.isSettingOverwritable('enableDatabaseFeature')) {
      this.enableDatabaseFeature = this.isTrueString(
        this.getSettingValue('EnableDatabaseFeature')
      );
    }

    if (this.isSettingOverwritable('enableTenantSwitching')) {
      this.enableTenantSwitching = this.isTrueString(
        this.getSettingValue('EnableTenantSwitching')
      );
    }

    if (this.isSettingOverwritable('brandingHelpUri')) {
      this.brandingHelpUri = this.getSettingValue('Branding:HelpUri');
    }

    if (this.isSettingOverwritable('showReportDatesInUtc')) {
      this.showReportDatesInUtc = this.isTrueString(
        this.getSettingValue('ShowReportDatesInUtc')
      );
    }

    if (
      this.getSettingValue('HideInviteUsers') &&
      this.isSettingOverwritable('hideInviteUsers')
    ) {
      this.hideInviteUsers = this.isTrueString(
        this.getSettingValue('HideInviteUsers')
      );
    }

    if (
      this.getSettingValue('ModelerUrl') &&
      this.isSettingOverwritable('modelerUrl')
    ) {
      this.modelerUrl = this.getSettingValue('ModelerUrl');
    }

    if (
      this.getSettingValue('Flowingly.Core.ServiceCatalog.Communications') &&
      this.isSettingOverwritable('signalrServerUrl')
    ) {
      this.signalrServerUrl =
        this.getSettingValue('Flowingly.Core.ServiceCatalog.Communications') +
        '/signalr';
    }

    if (
      this.getSettingValue('IntercomAppId') &&
      this.isSettingOverwritable('intercomId')
    ) {
      this.intercomId = this.getSettingValue('IntercomAppId');
    }

    if (
      this.getSettingValue('auth0:UseUniversalLogin') &&
      this.isSettingOverwritable('auth0UseUniversalLogin')
    ) {
      this.auth0UseUniversalLogin = this.isTrueString(
        this.getSettingValue('auth0:UseUniversalLogin')
      );
    }

    if (
      this.getSettingValue('FlowinglyEnvironment') &&
      this.isSettingOverwritable('flowinglyEnvironment')
    ) {
      this.flowinglyEnvironment = this.getSettingValue('FlowinglyEnvironment');
    }

    if (this.isSettingOverwritable('allowProcessMap')) {
      if (
        this.getSettingValue('AllowProcessMap') &&
        this.getSettingValue('AllowProcessMap').toString().toLowerCase() ===
          'false'
      ) {
        this.allowProcessMap = false;
      } else {
        this.allowProcessMap = true;
      }
    }

    if (
      this.getSettingValue('MaxSubCategories') &&
      this.isSettingOverwritable('maxSubCategories')
    ) {
      this.maxSubCategories = parseInt(
        this.getSettingValue('MaxSubCategories')
      );
    }

    const defaultHomePage = this.getSettingValue('DefaultHomePage')?.trim();
    if (defaultHomePage) {
      this.defaultHomePage = defaultHomePage;
    }

    if (
      this.getSettingValue('EnableExago') &&
      this.isSettingOverwritable('enableExago')
    ) {
      this.enableExago = this.isTrueString(this.getSettingValue('EnableExago'));

      if (this.getSettingValue('ExagoDefaultDashboardConfig')) {
        try {
          const temp = JSON.parse(
            this.getSettingValue('ExagoDefaultDashboardConfig')
          );

          if (temp['folder']) {
            this.exagoConfig.defaultDashboard.folder = temp['folder'];
          }

          if (temp['name']) {
            this.exagoConfig.defaultDashboard.name = temp['name'];
          }
        } catch (e) {
          console.error(
            'Problem parsing business setting ExagoDefaultDashboardConfig'
          );
        }
      }
    }

    if (
      this.getSettingValue('DisableAdminTeamEdit') &&
      this.isSettingOverwritable('disableAdminTeamEdit')
    ) {
      this.disableAdminTeamEdit = this.isTrueString(
        this.getSettingValue('DisableAdminTeamEdit')
      );
    }

    if (
      this.getSettingValue('DisableAdminUserEdit') &&
      this.isSettingOverwritable('disableAdminUserEdit')
    ) {
      this.disableAdminUserEdit = this.isTrueString(
        this.getSettingValue('DisableAdminUserEdit')
      );
    }

    if (
      this.getSettingValue('DisableAdminCategoryEdit') &&
      this.isSettingOverwritable('disableAdminCategoryEdit')
    ) {
      this.disableAdminCategoryEdit = this.isTrueString(
        this.getSettingValue('DisableAdminCategoryEdit')
      );
    }

    if (
      this.getSettingValue('DisableAdminDatabaseDelete') &&
      this.isSettingOverwritable('disableAdminDatabaseDelete')
    ) {
      this.disableAdminDatabaseDelete = this.isTrueString(
        this.getSettingValue('DisableAdminDatabaseDelete')
      );
    }

    if (
      this.getSettingValue('EnableReportOwner') &&
      this.isSettingOverwritable('enableReportOwner')
    ) {
      this.enableReportOwner = this.isTrueString(
        this.getSettingValue('EnableReportOwner')
      );
    }

    if (
      this.getSettingValue('DefaultDays') &&
      this.isSettingOverwritable('defaultDays')
    ) {
      this.defaultDays = +this.getSettingValue('DefaultDays');
    }

    if (
      this.getSettingValue('MaxReportReturnRows') &&
      this.isSettingOverwritable('maxReportReturnRows')
    ) {
      this.maxReportReturnRows = +this.getSettingValue('MaxReportReturnRows');
    }

    if (
      this.getSettingValue('MaximumWebhooksOnStep') &&
      this.isSettingOverwritable('maximumWebhooksOnStep')
    ) {
      this.maximumWebhooksOnStep =
        parseInt(this.getSettingValue('MaximumWebhooksOnStep')) || 5;
    }

    if (
      this.getSettingValue('EnablePublicProcessMaps') &&
      this.isSettingOverwritable('EnablePublicProcessMaps')
    ) {
      this.enablePublicProcessMaps = this.isTrueString(
        this.getSettingValue('EnablePublicProcessMaps')
      );
    }

    if (
      this.getSettingValue('EnableFlowDelete') &&
      this.isSettingOverwritable('EnableFlowDelete')
    ) {
      this.tryApplySetting('EnableFlowDelete', SettingValueType.boolean);
    }

    this.tryApplySetting('EnableFormFieldReCaptcha', SettingValueType.boolean);
    this.tryApplySetting('EnableHiddenLookupField', SettingValueType.boolean);
    this.tryApplySetting(
      'EnablePublicFormsAnonymous',
      SettingValueType.boolean
    );
    this.tryApplySetting('EnablePublicFormsSignIn', SettingValueType.boolean);
    this.tryApplySetting(
      'EnablePublishNotificationOption',
      SettingValueType.boolean
    );
    this.tryApplySetting('EnableFormFieldAddress', SettingValueType.boolean);
    this.tryApplySetting('EnableFormFieldBusiness', SettingValueType.boolean);
    this.tryApplySetting('EnableFieldIcons', SettingValueType.boolean);

    // TODO once switchover to new infrastructure is complete we can update
    // these settings instead of modifying them
    const legacyPath = '/Client/dist/';
    if (
      this.getSettingValue('RunnerClosedLogoUrl') &&
      this.isSettingOverwritable('runnerClosedLogoUrl')
    ) {
      const runnerClosedLogoUrl = this.getSettingValue('RunnerClosedLogoUrl');
      this.runnerClosedLogoUrl =
        runnerClosedLogoUrl.indexOf(legacyPath) > -1
          ? runnerClosedLogoUrl.split(legacyPath)[1]
          : runnerClosedLogoUrl;
    }

    if (
      this.getSettingValue('RunnerOpenLogoUrl') &&
      this.isSettingOverwritable('runnerOpenLogoUrl')
    ) {
      const runnerOpenLogoUrl = this.getSettingValue('RunnerOpenLogoUrl');
      this.runnerOpenLogoUrl =
        runnerOpenLogoUrl.indexOf(legacyPath) > -1
          ? runnerOpenLogoUrl.split(legacyPath)[1]
          : runnerOpenLogoUrl;
    }

    if (
      this.getSettingValue('GoogleTagManagerId') &&
      this.isSettingOverwritable('googleTagManagerId')
    ) {
      this.googleTagManagerId = this.getSettingValue('GoogleTagManagerId');
    }

    if (
      this.getSettingValue('FlowsInDateFilterDefaultRange') &&
      this.isSettingOverwritable('FlowsInDateFilterDefaultRange')
    ) {
      this.flowsInDateFilterDefaultRange = this.getSettingValue(
        'FlowsInDateFilterDefaultRange'
      );
    }

    if (
      this.getSettingValue('FlowsInDateFilterMaxRange') &&
      this.isSettingOverwritable('FlowsInDateFilterMaxRange')
    ) {
      this.flowsInDateFilterMaxRange = this.getSettingValue(
        'FlowsInDateFilterMaxRange'
      );
    }

    if (
      this.getSettingValue('Flowingly.Core.ServiceCatalog.Runner') &&
      this.isSettingOverwritable('runnerRehomeUrl')
    ) {
      this.runnerRehomeUrl = this.getSettingValue(
        'Flowingly.Core.ServiceCatalog.Runner'
      );
    }

    this.tryApplySetting(
      'MaxReturnRecords',
      SettingValueType.number,
      'maxReturnRecords'
    );
    this.tryApplySetting('EnableCircleCommunity', SettingValueType.boolean);

    this.setReady();
  }
}

angular.module('flowingly.services').constant('APP_CONFIG', new APP_CONFIG());

export type APP_CONFIGType = InstanceType<typeof APP_CONFIG>;
